import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import AttachmentPreviewContent, { getFileExtension } from 'components/Dashboard/components/AttachmentPreviewContent';
import SharedPdfPreview from 'components/ShareDocument/SharedPdfPreview';
import mobileCheck from 'components/Dashboard/helpers/mobileCheck';
import Loading from 'components/Login/Loading';
import useShareDocument from 'api/hooks/useShareDocument';

const getDocumentFaviconUrl = () => `${import.meta.env.VITE_APP_CDNURL}/images/document-scope-favicon.svg`;

interface SharedDocument {
  documentUrl?: string;
  filename?: string;
  backgroundColor?: string;
  organisationLogo?: string;
}

const ShareDocumentV2Page = () => {
  const { uuid } = useParams<{ uuid: string }>();
  const { getSharedDocumentV2 } = useShareDocument();
  const [mainDocument, setMainDocument] = useState<SharedDocument>({});

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [notFound, setNotFound] = useState<boolean>(false);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);

  const handleDownload = async () => {
    if (!mainDocument?.documentUrl || isDownloading) {
      return;
    }
    setIsDownloading(true);
    try {
      const response = await fetch(mainDocument?.documentUrl);
      if (!response.ok) {
        throw new Error(`Failed to download document. Status: ${response.status}`);
      }

      const blob = await response.blob();
      const blobURL = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = blobURL;
      anchor.style.display = 'none';

      if (mainDocument.filename?.length) {
        anchor.download = mainDocument.filename;
      }

      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      setTimeout(() => URL.revokeObjectURL(blobURL), 0);
    } catch (error) {
      console.error(error);
    } finally {
      setIsDownloading(false);
    }
  };

  const loadSharedDocument = useRef<((signal: AbortSignal) => Promise<void>) | null>(null);

  loadSharedDocument.current = async (signal) => {
    try {
      if (!uuid) {
        setNotFound(true);
        setIsLoading(false);
        document.title = 'Document not found';
        return;
      }

      setIsLoading(true);
      setNotFound(false);
      const response = await getSharedDocumentV2({ uuid, signal });
      if (response !== undefined) {
        const sharedDocument: SharedDocument = {
          documentUrl: response?.documentUrl,
          filename: response?.filename,
          backgroundColor: response?.backgroundColor,
          organisationLogo: response?.organisationLogo,
        };

        if (sharedDocument?.documentUrl) {
          setMainDocument(sharedDocument);
          setNotFound(false);

          if (sharedDocument.filename) {
            document.title = sharedDocument.filename;
          }

          const documentFaviconUrl = sharedDocument.organisationLogo || getDocumentFaviconUrl();
          document.querySelector('link[rel="icon"]')?.setAttribute('href', documentFaviconUrl);
          document.querySelector('link[rel="apple-touch-icon"]')?.setAttribute('href', documentFaviconUrl);
        } else {
          setNotFound(true);
          document.title = 'Document not found';
        }
        setIsLoading(false);
      }
    } catch {
      // apiClient's error interceptor already shows a toast
    }
  };

  useEffect(() => {
    document.getElementById('policymogul-cookie-item')?.remove();

    if ((window as any).Intercom) {
      (window as any).Intercom('update', { hide_default_launcher: true });
    }
    const controller = new AbortController();
    loadSharedDocument.current?.(controller.signal);

    return () => {
      controller.abort('Share document component unmounted.');
      if ((window as any).Intercom) {
        (window as any).Intercom('update', { hide_default_launcher: mobileCheck() });
      }
    };
  }, []);

  return (
    <>
      <div
        className='share-document-page main-content-wrapper px-3 py-5'
        style={{ backgroundColor: mainDocument.backgroundColor }}
      >
        {isLoading ? (
          <Loading />
        ) : (
          <>
            {notFound || !mainDocument.documentUrl ? (
              <div className='box-top-rounded bg-white p-4 p-lg-5 mx-auto' style={{ maxWidth: '720px' }}>
                <h1 className='title-h3-bold mb-3'>Document not found</h1>
                <p className='paragraph-general main-subtle-text mb-0'>
                  This share link is invalid, expired, or no longer available.
                </p>
              </div>
            ) : (
              <>
                <div className='d-flex align-items-end justify-content-between gap-15 mb-4 mb-lg-5'>
                  <img
                    src={`${mainDocument.organisationLogo}`}
                    alt='Scope logo'
                    style={{ width: '40px' }}
                    className='rounded'
                  />
                  <button
                    type='button'
                    className='general-button specific-x-padding-20 specific-y-padding-7 border-general border-grey-50 rounded-pill d-flex align-items-center hovered-button bg-main-white'
                    disabled={isDownloading}
                    onClick={handleDownload}
                  >
                    {isDownloading ? (
                      <i className='fas fa-spinner fa-spin' />
                    ) : (
                      <>
                        <span className='icon-download-attachment specific-right-margin-13' />
                        Download
                      </>
                    )}
                  </button>
                </div>
                <div className='share-document-preview border'>
                  {getFileExtension(mainDocument.filename, mainDocument.documentUrl) === 'pdf' ? (
                    <SharedPdfPreview url={mainDocument.documentUrl ?? ''} filename={mainDocument.filename} />
                  ) : (
                    <AttachmentPreviewContent
                      url={mainDocument?.documentUrl}
                      filename={mainDocument?.filename}
                      pageNumber={undefined}
                      onPageNumberChange={undefined}
                      onPdfLoadSuccess={undefined}
                      fullPdfPreview
                      containedPreview
                    />
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default ShareDocumentV2Page;
